import {
  HomeFilled,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import React, { useContext, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import styled from 'styled-components'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import GenericSvgIcon from 'components/GenericSvgIcon'
import { KeyboardShortcutKeyAnnotation } from 'components/KeyboardShortcutKeyAnnotation'
import {
  NavIconWrapper as IconWrapper,
  NavLabelRow,
  NavText,
  navRowCss,
} from 'components/navigation/nav-row-styles'
import { NavGroup } from 'components/navigation/NavGroup/NavGroup'
import { TooltipIconButton } from 'components/TooltipIconButton'
import { useAnnotationHighlightStore } from 'features/ask-yogi/annotation-highlight-store'
import { ASK_YOGI_URL } from 'features/ask-yogi/paths'
import { AuthContext } from 'features/auth'
import { useViewAsRoleContext } from 'features/auth/context'
import { PROFILE_URL } from 'features/profile/paths'
import { usePaletteShortcutDisplay } from 'features/project/features/command-palette/hooks/use-palette-shortcut-display'
import { useCommandPaletteStore } from 'features/project/features/command-palette/store'
import type { NavChicletConfig } from 'features/project/partials/project-layout/components/navigation/components/NavItemLink/NavItemLink'
import { NavItemLink } from 'features/project/partials/project-layout/components/navigation/components/NavItemLink/NavItemLink'
import { UserMenuButton } from 'features/project/partials/project-layout/components/navigation/UserMenuButton'
import {
  getNavigationItems,
  groupNavigationItems,
} from 'features/project/partials/project-layout/components/navigation/utils'
import { ViewAsRoleBanner } from 'features/project/partials/project-layout/components/navigation/ViewAsRoleBanner'
import { useProjectLayoutState } from 'features/project/partials/project-layout/hooks/use-project-layout-state'
import { useNav } from 'features/project/partials/project-layout/use-layout'
import type { FeatureFlagsResponse } from 'features/project/service/feature-flags-service'
import { isInitialFlow } from 'features/project/utils/project-status'
import { VIEW_AS_ROLE_SECTION_ID } from 'features/settings/features/access/components/view-as-role/ViewAsRole'
import { ACCESS_URL, SETTINGS_URL } from 'features/settings/paths'
import { TREND_DETECTION_URL } from 'features/trend-detection/paths'
import { GLOBAL_SHORTCUT_KEYS } from 'hooks/keyboard-shortcuts'
import { useShortcutDisplay } from 'hooks/use-keyboard-shortcuts'
import { getCurrentRouteName } from 'routes/detection'
import { HubSelector } from 'src/features/project-home/components/HubSelector'
import mixpanel from 'trackers/mixpanel'

// @ts-expect-error not used now but will be in future
const BETA_CHICLET: NavChicletConfig = {
  text: 'Beta',
  $tone: 'violet',
}

const NAV_CHICLETS: Record<string, NavChicletConfig> = {}

type NavigationProps = {
  isProjectLocked?: boolean
}

export const Navigation: React.FC<NavigationProps> = ({
  isProjectLocked = false,
}) => {
  // Get collapse state from context
  const { isMainNavCollapsed, toggleMainNav } = useNav()

  // global state
  const { projectId, details, feature_flags, projectsData } =
    useProjectLayoutState()
  const pageSettings = details?.clientSettings?.project?.pages

  // hooks
  const navigate = useNavigate()
  const location = useLocation()
  const projectBasePath = `/project/${projectId}`
  const authContext = useContext(AuthContext)
  const { previewedHubRole, clearPreview } = useViewAsRoleContext()
  const openPalette = useCommandPaletteStore((state) => state.open)
  const paletteShortcut = usePaletteShortcutDisplay()
  const shortcutDisplay = useShortcutDisplay(
    GLOBAL_SHORTCUT_KEYS.MAIN_NAV_TOGGLE,
  )

  // helper function to clean search params for Ask Yogi
  const getCleanSearchForAskYogi = (): string => {
    const searchParams = new URLSearchParams(location.search)
    searchParams.delete('share')
    searchParams.delete('conversation')
    const cleanSearch = searchParams.toString()
    return cleanSearch ? `?${cleanSearch}` : ''
  }

  // helper function to clean AskYogi-specific params when leaving AskYogi
  const getCleanSearchLeavingAskYogi = (): string => {
    const searchParams = new URLSearchParams(location.search)
    // Remove AskYogi-specific params
    searchParams.delete('conversation')
    searchParams.delete('message')
    searchParams.delete('share')
    searchParams.delete('showHistory')
    searchParams.delete('source')
    searchParams.delete('cuid')
    const cleanSearch = searchParams.toString()
    return cleanSearch ? `?${cleanSearch}` : ''
  }

  // Deep-link the "viewing as {role}" banner to the view-as control on the
  // access settings page, anchoring to its section so it scrolls into view.
  const navigateToViewAsControl = (): void => {
    navigate(
      `${projectBasePath}/${SETTINGS_URL}/${ACCESS_URL}#${VIEW_AS_ROLE_SECTION_ID}`,
    )
  }

  const cleanIssueAgentParams = (): string => {
    const searchParams = new URLSearchParams(location.search)
    searchParams.delete('shared')
    const cleanSearch = searchParams.toString()
    return cleanSearch ? `?${cleanSearch}` : ''
  }

  // Check if currently on AskYogi page
  const isOnAskYogi = location.pathname.includes(ASK_YOGI_URL)

  // Check if currently on Issue Agent page
  const isOnIssueAgent = location.pathname.includes(TREND_DETECTION_URL)

  // Query params to carry across navigation (excluding filter hash which is
  // now in the URL path, not the query string).
  const cleanSearch = useMemo(() => {
    const params = new URLSearchParams(location.search)
    params.delete('filters') // strip any leftover old-format param
    const str = params.toString()
    return str ? `?${str}` : ''
  }, [location.search])
  const navigationItems = useMemo(
    () =>
      getNavigationItems({
        feature_flags: feature_flags as FeatureFlagsResponse | undefined,
        pageSettings,
        isYogiUser: !!authContext?.userDetails?.yogi_user,
        projectId,
      }).filter((el) => el.enabled),
    [
      feature_flags,
      pageSettings,
      authContext?.userDetails?.yogi_user,
      projectId,
    ],
  )

  // Determine the current page name for sidebar highlighting.
  // getCurrentRouteName is hash-aware: for /feedback/{hash} it returns 'feedback',
  // not the hash. Without this, the sidebar wouldn't highlight the correct item
  // because the raw last path segment would be the hash, not the page name.
  const currentRouteName = getCurrentRouteName(location.pathname)

  // Tracks which feature the user is hovering over in an agent annotation.
  // Selector is narrow so Navigation only re-renders when the highlighted feature changes.
  const highlightedFeature = useAnnotationHighlightStore(
    (s) => s.highlight?.feature ?? null,
  )

  // Whether any nav item (top-level or nested inside a group) matches the
  // current page, to highlight it. Direct url comparison rather than
  // array-index comparison, since a group's children no longer share a flat
  // index with their siblings.
  const hasActiveNavItem = navigationItems.some(
    (el) => el.url === currentRouteName,
  )

  // Check if we're on a settings-related page (profile or settings)
  const settingsUrls = [SETTINGS_URL, PROFILE_URL]
  const isOnSettingsPage = settingsUrls.includes(currentRouteName || '')

  // Home is only selected when not on any nav item AND not on settings pages
  const isHomeSelected = !hasActiveNavItem && !isOnSettingsPage

  const hubHasCompletedPreprocessingAndCanDisplayMenuItems = !isInitialFlow(
    details?.state,
  )

  // Coalesces consecutive same-group items (e.g. the "Analyze" group) into a
  // single collapsible entry; see groupNavigationItems for details.
  const navigationEntries = useMemo(
    () => groupNavigationItems(navigationItems),
    [navigationItems],
  )

  // Which groups are collapsed, keyed by group id. Absent/false = expanded.
  // Plain component state, not persisted — matches isMainNavCollapsed.
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({})
  const toggleGroup = (groupId: string): void =>
    setCollapsedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }))

  // Shared click handling for every nav item (top-level or nested): clean up
  // page-specific query params depending on where we're navigating from/to,
  // then navigate and track. Extracted from the per-item render below so
  // group children and top-level items share one implementation.
  const handleNavClick =
    (item: { title: string; url: string }) => (e: React.MouseEvent) => {
      if (e.metaKey || e.ctrlKey) return
      e.preventDefault()
      let searchToUse = cleanSearch
      if (item.url === ASK_YOGI_URL) {
        // Clean params when going TO AskYogi
        searchToUse = getCleanSearchForAskYogi()
      } else if (item.url === TREND_DETECTION_URL) {
        // Clean params when going TO Issue Agent to show list view
        searchToUse = cleanIssueAgentParams()
      } else if (isOnAskYogi) {
        // Clean AskYogi-specific params when leaving AskYogi
        searchToUse = getCleanSearchLeavingAskYogi()
      } else if (isOnIssueAgent) {
        // Clean Issue Agent-specific params when leaving Issue Agent
        searchToUse = cleanIssueAgentParams()
      }
      navigate(`${projectBasePath}/${item.url}` + searchToUse)
      mixpanel.track(item.title, {
        proj_uuid: projectId,
      })
    }

  return (
    <Container>
      <TopRow $isCollapsed={isMainNavCollapsed}>
        {!isMainNavCollapsed && (
          <HubSelector
            isCollapsed={isMainNavCollapsed}
            details={details}
            projects={(
              projectsData as
                | { data?: Array<{ uuid: string; title: string }> }
                | undefined
            )?.data?.map((p) => ({
              proj_uuid: p.uuid,
              title: p.title,
            }))}
          />
        )}
        <TooltipIconButton
          tooltipTitle={
            <>
              {isMainNavCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}{' '}
              <KeyboardShortcutKeyAnnotation
                shortcut={shortcutDisplay}
                style={{ marginLeft: '4px' }}
              />
            </>
          }
          tooltipSide="right"
          icon={
            isMainNavCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
          }
          onClick={toggleMainNav}
          height={32}
          width={32}
          iconFontSize="14px"
        />
      </TopRow>
      <NavigationList>
        <Tooltip content={isMainNavCollapsed ? 'Search' : ''} side="right">
          <SidebarSearchButton
            type="button"
            onClick={openPalette}
            aria-label="Open command palette"
            $isActive={false}
            $isCollapsed={isMainNavCollapsed}
            $isHighlighted={false}
          >
            <IconWrapper>
              <GenericSvgIcon SvgComponent={SearchOutlined} />
            </IconWrapper>
            <NavLabelRow $hidden={isMainNavCollapsed}>
              <NavText>Search</NavText>
              <SidebarSearchKbd>{paletteShortcut}</SidebarSearchKbd>
            </NavLabelRow>
          </SidebarSearchButton>
        </Tooltip>
        {/* hide_ask_yogi: This is to hide Ask Yogi for Disney. Delete these checks once they approve Ask Yogi usage. */}
        {!isProjectLocked &&
          !(feature_flags as Record<string, boolean> | undefined)?.[
            'hide_ask_yogi'
          ] && (
            <Tooltip content={isMainNavCollapsed ? 'Home' : ''} side="right">
              <StyledLink
                key={'home'}
                to={`${projectBasePath}${isOnAskYogi ? getCleanSearchLeavingAskYogi() : isOnIssueAgent ? cleanIssueAgentParams() : cleanSearch}`}
                $isActive={isHomeSelected}
                $isCollapsed={isMainNavCollapsed}
                $isHighlighted={false}
                onClick={(e: React.MouseEvent) => {
                  if (e.metaKey || e.ctrlKey) return
                  e.preventDefault()
                  // Clean page-specific params when leaving those pages
                  let searchToUse = cleanSearch
                  if (isOnAskYogi) {
                    searchToUse = getCleanSearchLeavingAskYogi()
                  } else if (isOnIssueAgent) {
                    searchToUse = cleanIssueAgentParams()
                  }
                  navigate(`${projectBasePath}` + searchToUse)
                  mixpanel.track('home', {
                    proj_uuid: projectId,
                  })
                }}
              >
                <IconWrapper>
                  <GenericSvgIcon SvgComponent={HomeFilled} />
                </IconWrapper>
                <NavText $hidden={isMainNavCollapsed}>Home</NavText>
              </StyledLink>
            </Tooltip>
          )}

        {!isProjectLocked &&
          hubHasCompletedPreprocessingAndCanDisplayMenuItems &&
          navigationEntries.map((entry) => {
            if (entry.kind === 'item') {
              const { item } = entry
              return (
                <NavItemLink
                  key={item.url}
                  item={item}
                  to={`${projectBasePath}/${item.url}` + cleanSearch}
                  isActive={item.url === currentRouteName}
                  isCollapsed={isMainNavCollapsed}
                  isHighlighted={highlightedFeature === item.url}
                  chiclet={NAV_CHICLETS[item.url]}
                  onClick={handleNavClick(item)}
                />
              )
            }
            return (
              <NavGroup
                key={entry.id}
                title={entry.title}
                svgComponent={entry.svgComponent}
                isExpanded={!collapsedGroups[entry.id]}
                isCollapsed={isMainNavCollapsed}
                onToggle={() => toggleGroup(entry.id)}
              >
                {entry.children.map((child) => (
                  <NavItemLink
                    key={child.url}
                    item={child}
                    to={`${projectBasePath}/${child.url}` + cleanSearch}
                    isActive={child.url === currentRouteName}
                    isCollapsed={isMainNavCollapsed}
                    isHighlighted={highlightedFeature === child.url}
                    chiclet={NAV_CHICLETS[child.url]}
                    onClick={handleNavClick(child)}
                  />
                ))}
              </NavGroup>
            )
          })}

        <BottomGroup>
          <ViewAsRoleBanner
            previewedHubRole={previewedHubRole}
            onNavigate={navigateToViewAsControl}
            onExit={clearPreview}
            isCollapsed={isMainNavCollapsed}
          />
          <UserMenuButton isCollapsed={isMainNavCollapsed} />
        </BottomGroup>
      </NavigationList>
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  height: 100%;
  margin: 12px 0;
  padding: 0 8px;
  display: flex;
  gap: 8px;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  overflow: auto;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`

const TopRow = styled.div<{ $isCollapsed?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 4px;
  overflow: hidden;
`

// Opens the command palette. Composed from the shared navRowCss so it stays
// pixel-identical to the nav item rows (icon-only when collapsed).
const SidebarSearchButton = styled.button<{
  $isActive: boolean
  $isCollapsed: boolean
  $isHighlighted?: boolean
}>`
  ${navRowCss}
  border: none;
  font-family: inherit;
`

const SidebarSearchKbd = styled.kbd`
  font-family: inherit;
  font-size: 11px;
  color: #6b7280;
`

const NavigationList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  gap: 2px;
  overflow: hidden;
`

// Pins the view-as banner + user menu to the bottom of the sidebar, with the
// banner sitting directly above the user/settings line.
const BottomGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  gap: 8px;
  margin-top: auto;
`

// Home is rendered inline (not via NavItemLink) rather than through
// navigationItems, so it needs its own Link wrapper — composed from the
// shared navRowCss so it stays pixel-identical to every other row.
const StyledLink = styled(Link)<{
  $isActive: boolean
  $isCollapsed: boolean
  $isHighlighted?: boolean
}>`
  ${navRowCss}
`
